import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { LineChart } from "@mui/x-charts/LineChart";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";

import { THEME_COLOR_CONFIG } from "config/themeColors";
import {
  buildTrendDataset,
  computeRelativeYDomain,
  filterPointsByRange,
  formatMeasuredValue,
  getLatestReadingMeta,
  pickSparklineEntries,
} from "./salifertTrendsChartUtils";
import { scrollRegionNestedSx } from "utils/scrollRegion";
import { useSurfaceTheme } from "utils/surfaceTheme";

function shortCode(code, symbols) {
  return symbols[code] || String(code || "").toUpperCase();
}

function SparklineCard({ entry, color, range, targetByCode }) {
  const points = filterPointsByRange(entry.data, range);
  const values = points.map((p) =>
    p.rawValue != null && Number.isFinite(p.rawValue) ? p.rawValue : p.value,
  );
  const { valueText, percentText } = getLatestReadingMeta(entry, targetByCode);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.5 },
        height: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          lineHeight: 1.2,
          fontSize: { xs: "0.65rem", sm: "0.75rem" },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {entry.def.title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          lineHeight: 1.15,
          my: { xs: 0.35, sm: 0.5 },
          fontSize: { xs: "1rem", sm: "1.5rem" },
        }}
      >
        {valueText}
      </Typography>
      {percentText ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mb: 0.35,
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {percentText}
        </Typography>
      ) : null}
      {values.length > 1 ? (
        <SparkLineChart
          data={values}
          height={36}
          colors={[color]}
          showHighlight
          showTooltip
        />
      ) : (
        <Box sx={{ height: { xs: 36, sm: 44 } }} />
      )}
    </Paper>
  );
}

SparklineCard.propTypes = {
  entry: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  range: PropTypes.string.isRequired,
  targetByCode: PropTypes.instanceOf(Map).isRequired,
};

function LegendLabel({ code, color, valueText, percentText, symbols }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {shortCode(code, symbols)}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ display: "block", pl: 1.75, lineHeight: 1.35 }}
      >
        {valueText}
      </Typography>
      {percentText ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", pl: 1.75, lineHeight: 1.35 }}
        >
          {percentText}
        </Typography>
      ) : null}
    </Box>
  );
}

LegendLabel.propTypes = {
  code: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  valueText: PropTypes.string.isRequired,
  percentText: PropTypes.string,
  symbols: PropTypes.object.isRequired,
};

/** Compact inline legend for mobile parameter chips (wraps within each chip). */
function CompactLegendLabel({ code, color, valueText, percentText, symbols }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 0.5,
        rowGap: 0.15,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        component="span"
        sx={{ fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap" }}
      >
        {shortCode(code, symbols)}
      </Typography>
      <Typography
        variant="caption"
        component="span"
        color="text.primary"
        sx={{ lineHeight: 1.3, wordBreak: "break-word" }}
      >
        {valueText}
      </Typography>
      {percentText ? (
        <Typography
          variant="caption"
          component="span"
          color="text.secondary"
          sx={{ lineHeight: 1.3, wordBreak: "break-word" }}
        >
          {percentText}
        </Typography>
      ) : null}
    </Box>
  );
}

CompactLegendLabel.propTypes = LegendLabel.propTypes;

export default function SalifertTrendsMuiDashboard({
  chartSeries,
  byTestWithData,
  hiddenCodes,
  onHiddenCodesChange,
  range,
  scaleMode,
  targetByCode,
  legendSymbols,
  downMD,
}) {
  const theme = useTheme();
  const { borderColor: subtleBorderColor } = useSurfaceTheme("subtle");
  const mobileParameterChipSx = useMemo(
    () => ({
      m: 0,
      flex: "0 1 auto",
      maxWidth: "100%",
      alignItems: "flex-start",
      border: 1,
      borderColor: subtleBorderColor,
      borderRadius: 1,
      px: 0.75,
      py: 0.4,
      mr: 0,
      "& .MuiFormControlLabel-label": {
        minWidth: 0,
      },
    }),
    [subtleBorderColor],
  );
  const palette = THEME_COLOR_CONFIG.salifert.trends.linePalette;
  const divider = theme.vars?.palette?.divider || theme.palette.divider;

  const chartAreaRef = useRef(null);
  const [mainChartHeight, setMainChartHeight] = useState(0);

  const colorByCode = useMemo(() => {
    const map = new Map();
    byTestWithData.forEach((entry, idx) => {
      map.set(entry.def.code, palette[idx % palette.length]);
    });
    return map;
  }, [byTestWithData, palette]);

  const { dataset, series, visible } = useMemo(
    () => buildTrendDataset(chartSeries, hiddenCodes, range),
    [chartSeries, hiddenCodes, range],
  );

  const lineSeries = useMemo(
    () =>
      (visible || []).map((entry) => {
        const code = entry.def.code;
        const unit = entry.def.primaryUnit || "";
        return {
          dataKey: code,
          label: shortCode(code, legendSymbols),
          showMark: true,
          shape: "circle",
          connectNulls: true,
          color: colorByCode.get(code),
          curve: "natural",
          valueFormatter: (value, context) => {
            const row = dataset[context?.dataIndex];
            const raw = row?.[`${code}__raw`];
            if (raw != null && Number.isFinite(raw)) {
              return formatMeasuredValue(raw, unit);
            }
            return formatMeasuredValue(value, unit);
          },
        };
      }),
    [visible, legendSymbols, colorByCode, dataset],
  );

  const sparklineEntries = useMemo(
    () => pickSparklineEntries(chartSeries, 3),
    [chartSeries],
  );

  const legendMetaByCode = useMemo(() => {
    const map = new Map();
    chartSeries.forEach((entry) => {
      map.set(entry.def.code, getLatestReadingMeta(entry, targetByCode));
    });
    return map;
  }, [chartSeries, targetByCode]);

  const relativeYDomain = useMemo(() => {
    if (scaleMode !== "relative" || !visible?.length) return null;
    return computeRelativeYDomain(
      dataset,
      visible.map((entry) => entry.def.code),
    );
  }, [scaleMode, dataset, visible]);

  const hasLineChart = dataset.length > 0 && lineSeries.length > 0;

  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el || !hasLineChart) return undefined;

    const update = () => {
      const next = Math.floor(el.clientHeight);
      if (next > 0) {
        setMainChartHeight((prev) => (prev === next ? prev : next));
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasLineChart, downMD, dataset.length, lineSeries.length]);

  const allSelected = hiddenCodes.length === 0 && byTestWithData.length > 0;
  const someSelected =
    hiddenCodes.length > 0 && hiddenCodes.length < byTestWithData.length;

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ flexShrink: 0 }}>
        {sparklineEntries.map((entry) => (
          <Grid key={entry.def.code} size={{ xs: 4, sm: 4 }}>
            <SparklineCard
              entry={entry}
              color={colorByCode.get(entry.def.code) || palette[0]}
              range={range}
              targetByCode={targetByCode}
            />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          overflow: "hidden",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: { xs: 1, md: 1 },
            minWidth: 0,
            minHeight: { xs: 200, md: 0 },
            maxHeight: { xs: "none", md: "none" },
            p: { xs: 1, md: 1.5 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="subtitle2">Parameter trends</Typography>
            {scaleMode === "relative" && relativeYDomain ? (
              <Typography variant="caption" color="text.secondary">
                Y-axis centered on 100% target ({relativeYDomain.min.toFixed(0)}
                –{relativeYDomain.max.toFixed(0)}%)
              </Typography>
            ) : null}
          </Stack>
          {dataset.length === 0 || lineSeries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
              No visible data in this range. Enable legend items or switch to
              Absolute mode if targets are missing.
            </Typography>
          ) : (
            <Box
              ref={chartAreaRef}
              sx={{
                flex: 1,
                minHeight: downMD ? 180 : 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              {mainChartHeight > 0 ? (
                <LineChart
                  hideLegend
                  dataset={dataset}
                  grid={{ horizontal: true, vertical: false }}
                  xAxis={[
                    {
                      scaleType: "time",
                      dataKey: "date",
                      tickSize: 7,
                      disableLine: true,
                    },
                  ]}
                  yAxis={[
                    {
                      tickSize: 7,
                      disableLine: true,
                      ...(relativeYDomain
                        ? {
                            min: relativeYDomain.min,
                            max: relativeYDomain.max,
                            domainLimit: "strict",
                          }
                        : {}),
                      valueFormatter: (v) =>
                        scaleMode === "relative"
                          ? `${Number(v).toFixed(0)}%`
                          : String(v),
                    },
                  ]}
                  series={lineSeries}
                  height={mainChartHeight}
                  margin={{
                    top: 16,
                    bottom: 28,
                    left: 8,
                    right: 16,
                  }}
                  sx={{
                    "& .MuiChartsGrid-line": {
                      strokeDasharray: "4 4",
                      stroke: divider,
                    },
                    "& .MuiChartsAxis-root.MuiChartsAxis-directionX .MuiChartsAxis-tick":
                      { stroke: "transparent" },
                    "& .MuiChartsAxis-root.MuiChartsAxis-directionY .MuiChartsAxis-tick":
                      { stroke: "transparent" },
                    "& .MuiMarkElement-root": {
                      transform: "scale(0.55)",
                      transformOrigin: "center",
                      transformBox: "fill-box",
                      strokeWidth: 1.25,
                    },
                  }}
                >
                  {scaleMode === "relative" ? (
                    <ChartsReferenceLine
                      y={100}
                      label="Target (100%)"
                      lineStyle={{
                        stroke: theme.palette.text.secondary,
                        strokeDasharray: "6 4",
                      }}
                    />
                  ) : null}
                </LineChart>
              ) : null}
            </Box>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            width: { xs: "100%", md: 248 },
            flex: { xs: "0 0 auto", md: "0 0 auto" },
            flexShrink: 0,
            alignSelf: { md: "stretch" },
            minHeight: 0,
            height: { md: "100%" },
            maxHeight: { xs: "min(42vh, 360px)", md: "100%" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 1.25,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.75, display: "block", flexShrink: 0 }}
          >
            Parameters
          </Typography>
          <Box
            data-app-scroll-region={downMD ? "" : undefined}
            sx={{
              flex: 1,
              minHeight: 0,
              pr: downMD ? 0 : 0.25,
              ...(downMD
                ? scrollRegionNestedSx(1)
                : {
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                  }),
            }}
          >
            {downMD ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  alignContent: "flex-start",
                  gap: 0.75,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                  }
                  label="Select all"
                  sx={mobileParameterChipSx}
                  onChange={(_, checked) =>
                    onHiddenCodesChange(
                      checked
                        ? []
                        : byTestWithData.map((entry) => entry.def.code),
                    )
                  }
                />
                {byTestWithData.map((entry) => {
                  const checked = !hiddenCodes.includes(entry.def.code);
                  const color = colorByCode.get(entry.def.code) || palette[0];
                  const meta = legendMetaByCode.get(entry.def.code) || {
                    valueText: "—",
                    percentText: null,
                  };
                  return (
                    <FormControlLabel
                      key={entry.def.code}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          sx={{
                            color,
                            "&.Mui-checked": { color },
                            py: 0.15,
                          }}
                          onChange={(_, nextChecked) =>
                            onHiddenCodesChange(
                              nextChecked
                                ? hiddenCodes.filter(
                                    (c) => c !== entry.def.code,
                                  )
                                : [...hiddenCodes, entry.def.code],
                            )
                          }
                        />
                      }
                      label={
                        <CompactLegendLabel
                          code={entry.def.code}
                          color={color}
                          valueText={meta.valueText}
                          percentText={meta.percentText}
                          symbols={legendSymbols}
                        />
                      }
                      sx={mobileParameterChipSx}
                    />
                  );
                })}
              </Box>
            ) : (
              <Stack spacing={0.75}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                  }
                  label="Select all"
                  sx={{ m: 0, alignItems: "flex-start" }}
                  onChange={(_, checked) =>
                    onHiddenCodesChange(
                      checked
                        ? []
                        : byTestWithData.map((entry) => entry.def.code),
                    )
                  }
                />
                {byTestWithData.map((entry) => {
                  const checked = !hiddenCodes.includes(entry.def.code);
                  const color = colorByCode.get(entry.def.code) || palette[0];
                  const meta = legendMetaByCode.get(entry.def.code) || {
                    valueText: "—",
                    percentText: null,
                  };
                  return (
                    <FormControlLabel
                      key={entry.def.code}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          sx={{
                            color,
                            "&.Mui-checked": { color },
                            mt: 0.25,
                          }}
                          onChange={(_, nextChecked) =>
                            onHiddenCodesChange(
                              nextChecked
                                ? hiddenCodes.filter(
                                    (c) => c !== entry.def.code,
                                  )
                                : [...hiddenCodes, entry.def.code],
                            )
                          }
                        />
                      }
                      label={
                        <LegendLabel
                          code={entry.def.code}
                          color={color}
                          valueText={meta.valueText}
                          percentText={meta.percentText}
                          symbols={legendSymbols}
                        />
                      }
                      sx={{ m: 0, alignItems: "flex-start" }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
}

SalifertTrendsMuiDashboard.propTypes = {
  chartSeries: PropTypes.arrayOf(PropTypes.object).isRequired,
  byTestWithData: PropTypes.arrayOf(PropTypes.object).isRequired,
  hiddenCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onHiddenCodesChange: PropTypes.func.isRequired,
  range: PropTypes.string.isRequired,
  scaleMode: PropTypes.string.isRequired,
  targetByCode: PropTypes.instanceOf(Map).isRequired,
  legendSymbols: PropTypes.object.isRequired,
  downMD: PropTypes.bool.isRequired,
};
